// ============================================================================
// ROOM CATEGORY SERVICE
// ============================================================================
// Business logic and API calls for Room Category module

import { get, post, put, del } from '../client/axiosClient';
import { roomCategoryEndpoints } from '../endpoints/roomCategory.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  RoomCategory,
  CreateRoomCategoryRequest,
  UpdateRoomCategoryRequest,
  RoomCategorySearchRequest,
} from '../types';

/**
 * Room Category Service Class
 */
class RoomCategoryService {
  /**
   * Get all room categories (paginated)
   */
  async getAll(params?: RoomCategorySearchRequest): Promise<PaginatedResponse<RoomCategory>> {
    const endpoint = params
      ? buildEndpointWithQuery('/RoomCategory', params)
      : roomCategoryEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get room category by ID
   */
  async getById(id: ID): Promise<ApiResponse<RoomCategory>> {
    const response = await get<ApiResponse<RoomCategory>>(roomCategoryEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get room category by code
   */
  async getByCode(code: string, propertyId?: string): Promise<ApiResponse<RoomCategory>> {
    let endpoint = roomCategoryEndpoints.getByCode(code);
    if (propertyId) {
      endpoint = `${endpoint}?propertyId=${propertyId}`;
    }
    const response = await get<ApiResponse<RoomCategory>>(endpoint);
    return response.data;
  }

  /**
   * Create new room category
   */
  async create(data: CreateRoomCategoryRequest): Promise<ApiResponse<RoomCategory>> {
    const response = await post<ApiResponse<RoomCategory>>(
      roomCategoryEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update room category
   */
  async update(id: ID, data: UpdateRoomCategoryRequest): Promise<ApiResponse<RoomCategory>> {
    const response = await put<ApiResponse<RoomCategory>>(
      roomCategoryEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete room category
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(roomCategoryEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate room category code uniqueness
   */
  async validateCode(code: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = roomCategoryEndpoints.validateCode(code);
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) {
      endpoint = `${endpoint}?${queryParts.join('&')}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }

  /**
   * Validate room category name uniqueness
   */
  async validateName(name: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = roomCategoryEndpoints.validateName(encodeURIComponent(name));
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) {
      endpoint = `${endpoint}?${queryParts.join('&')}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const roomCategoryService = new RoomCategoryService();
export default roomCategoryService;
